import type { HTMLAttributes, ReactNode } from "react";
import { useMemo, useState } from "react";
import { useId } from "react-aria";

import { useI18n } from "../use-i18n";
import { cn } from "../utils";
import { NcButton } from "./nc-button";

interface NcReadMoreProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode | string;
  summary?: ReactNode | string;
  summaryLength?: number;
  moreLabel?: string | ReactNode;
  lessLabel?: string | ReactNode;
}

export const NcReadMore = ({
  children,
  summary,
  summaryLength = 200,
  moreLabel,
  lessLabel,
  className,
  ...props
}: NcReadMoreProps) => {
  const { t } = useI18n();
  const sectionId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const useAutoSummary = useMemo(() => typeof children === "string", [children]);
  const longEnough = String(children).length > summaryLength;

  if (useAutoSummary && !longEnough) {
    // no toggle if not exceeding summary length
    return (
      <div data-nc="NcReadMore" {...props}>
        {children}
      </div>
    );
  }
  const displaySummary = useAutoSummary ? `${String(children).slice(0, summaryLength)}…` : summary;
  return (
    <div
      data-nc="NcReadMore"
      id={sectionId}
      className={cn("flex-wrap whitespace-pre-wrap", className)}
      aria-live="polite"
      {...props}
    >
      {isExpanded ? children : displaySummary}
      &nbsp;
      <NcButton
        variant="link"
        className="order-10 font-normal"
        onPress={() => setIsExpanded(!isExpanded)}
        aria-controls={sectionId}
        aria-expanded={isExpanded}
      >
        {isExpanded ? lessLabel || t("read less") : moreLabel || t("read more")}
      </NcButton>
    </div>
  );
};
